import { AuthenticationMethod, IdentificationState } from 'auth';
import { getCurrentAuthMethod } from 'auth/auth-methods/current-auth-method';
import { TokenResponse } from 'auth/types/token-response.type';
import { UserInfo } from 'auth/types/user-info.type';
import { useConfig } from 'providers/config/ConfigProvider';
import { useState, useCallback, useEffect } from 'react';
import { logger } from '@sanlam-glacier/app-common/modules/logger';

type UseAuthenticationResult = {
  isError: boolean;
  isAuthLoading: boolean;
};

type UseAuthenticationProps = {
  token: TokenResponse | undefined;
  signedInUser: UserInfo | undefined;
  authApi: AuthenticationMethod;
  identificationState: IdentificationState;
  setIdentificationState: React.Dispatch<React.SetStateAction<IdentificationState>>;
};

export const useAuthentication = ({
  token,
  signedInUser,
  authApi,
  identificationState,
  setIdentificationState
}: UseAuthenticationProps): UseAuthenticationResult => {
  const env = useConfig();

  const [isError, setIsError] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(true);

  const pageLoadAuthenticate = useCallback(async () => {
    const { isUsingIbmGrant, isUsingWebseal, isUsingGateway, isUsingAfSso } =
      getCurrentAuthMethod(env);

    if (isUsingIbmGrant || isUsingWebseal) {
      const isSuccess = await authApi.login();
      setIsError(!isSuccess);
    }
    if ((isUsingGateway || isUsingAfSso) && identificationState === 'authenticated') {
      const isSuccess = await authApi.getAccess(token!);
      setIsError(!isSuccess);
    }
  }, [env, token, identificationState]);

  useEffect(() => {
    pageLoadAuthenticate()
      .catch((error) => {
        logger.error({ message: 'Error authenticating user', object: error });
        setIsError(true);
      })
      .finally(() => {
        setIsAuthLoading(false);
      });
  }, [pageLoadAuthenticate]);

  useEffect(() => {
    const isUserTokenValid = token && token.token_expiry_time > new Date();
    const { isUsingWebseal } = getCurrentAuthMethod(env);
    if (isUserTokenValid || isUsingWebseal) setIdentificationState('authenticated');
    else setIdentificationState('unidentified');
  }, [env, signedInUser, token]);

  return {
    isError,
    isAuthLoading
  };
};
